import { List, ListItem, ListItemText, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { useGetLaborMatrices } from '../../../hooks/useGetLaborMatrices/useGetLaborMatrices'
import { useGetPartsMatrices } from '../../../hooks/useGetPartsMatrices/useGetPartsMatrices'
import { getInventoryPartName } from '../../../hooks/useTransformServiceItems/useTransformServiceItems'
import { useGetAllInventoryPartsQuery } from '../../../redux/api'
import { useGetCannedJobExpandedQuery } from '../../../redux/api/cannedJobApi'
import { CannedJobExpanded } from '../../../sdk'
import { formatCurrency } from '../../../utils'
import {
  getPartAvailableQuantity,
  getPartReceivedQuantity
} from '../../../utils/convertFluidsUnits/convertFluidsUnits'
import { getCannedJobTotal } from '../../../utils/getCannedJobTotal/getCannedJobTotal'
import { ExpandPanel } from '../../SharedUI/ExpandPanel'
import { ModifiedCannedJobExpanded } from '../useLookupServiceDialog/transformSelectedJob'
import { CustomListItem } from './CustomListItem'
export const CannedJobItemUI = ({
  cannedJob,
  handleClick,
  handleClickSub,
  handleClickRelated,
  selectedCanned,
  selectedRelated
}: {
  cannedJob: CannedJobExpanded
  handleClick: (value: boolean) => void
  handleClickSub?: (value: boolean, partId: string) => void
  handleClickRelated?: (value: boolean, cannedJobId: string) => void
  selectedCanned: ModifiedCannedJobExpanded | undefined
  selectedRelated?: string[]
}) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()

  const { allLaborMatrixEntries } = useGetLaborMatrices([])
  const { allPartsMatrices } = useGetPartsMatrices([])
  const cannedJobTotal = useMemo(() => {
    if (!allLaborMatrixEntries || !allPartsMatrices) {
      return ''
    }
    const total = getCannedJobTotal(
      cannedJob,
      allLaborMatrixEntries,
      allPartsMatrices
    )
    return total === 'TBD' ? 'TBD' : formatCurrency(total, true)
  }, [cannedJob, allLaborMatrixEntries, allPartsMatrices])

  const { data: relatedCannedJobExpanded } = useGetCannedJobExpandedQuery(
    {
      authorization: accessToken,
      shop: shopId,
      cannedJobs: cannedJob.related!.map((r) => r.info!.child!)
    },
    {
      skip: cannedJob.related === undefined || cannedJob.related?.length === 0
    }
  )

  const { data: shopInventory } = useGetAllInventoryPartsQuery(
    {
      authorization: accessToken,
      shop: shopId,
      stockedOnly: false,
      cannedJobId: [cannedJob.entry!.id!]
    },
    { skip: !cannedJob.entry?.id }
  )

  return (
    <ExpandPanel
      id={cannedJob.entry!.id!}
      singleSelect={false}
      handleClick={handleClick}
      checked={!!selectedCanned}
      mainElements={
        <Stack direction={'row'} justifyContent="space-between">
          <Stack
            sx={{ width: '100%' }}
            direction={'column'}
            justifyContent="space-between">
            <Typography variant="subtitle1">
              {cannedJob.entry!.info!.name!}
            </Typography>
            {cannedJob.entry!.info!.description && (
              <Typography>{cannedJob.entry!.info!.description}</Typography>
            )}
          </Stack>
          <Typography variant="subtitle1">{cannedJobTotal}</Typography>
        </Stack>
      }
      hiddenElements={
        <>
          <List disablePadding>
            {cannedJob.customLabor!.length + cannedJob.motorLabor!.length >
              0 && (
              <ListItem selected={true}>
                <Typography variant="subtitle2">Labor</Typography>
              </ListItem>
            )}
            {[
              ...cannedJob.customLabor!.map((l) => ({
                id: l.id!,
                name: l.info!.name!,
                secondaryText: 'Custom'
              })),
              ...cannedJob.motorLabor!.map((l) => ({
                id: l.id!,
                name: l.info!.name!,
                secondaryText: 'Motor'
              }))
            ].map((data) => (
              <CustomListItem
                key={data.id!}
                id={data.id!}
                data={data}
                handleClick={handleClickSub || (() => {})}
                checked={
                  !!(selectedCanned && !selectedCanned.unSelected.has(data.id!))
                }
                disableSelectSubItem={!handleClickSub}
              />
            ))}
            {cannedJob.customParts!.length +
              cannedJob.partLookups!.length +
              cannedJob.inventoryParts!.length +
              cannedJob.motorFluids!.length >
              0 && (
              <ListItem selected={true}>
                <Typography variant="subtitle2">Parts</Typography>
              </ListItem>
            )}
            {shopInventory &&
              [
                ...cannedJob.customParts!.map((p) => ({
                  id: p.id!,
                  name: p.info!.name!,
                  quantity: p.info!.quantity!,
                  secondaryText: 'Custom'
                })),
                ...cannedJob.partLookups!.map((p) => ({
                  id: p.id!,
                  name: p.info!.searchTerm!,
                  quantity: undefined,
                  secondaryText: 'Lookup'
                })),
                ...cannedJob.inventoryParts!.map((p) => ({
                  id: p.id!,
                  name: getInventoryPartName(
                    cannedJob.idToInventoryParts![p.info!.inventoryPart!]!
                  ),
                  quantity: p.info!.quantity!,
                  secondaryText: `Inventory: ${getPartReceivedQuantity(
                    p.info!.inventoryPart!,
                    shopInventory
                  )} / ${getPartAvailableQuantity(
                    p.info!.inventoryPart!,
                    shopInventory
                  )}`
                })),
                ...cannedJob.inventoryProducts!.map((p) => ({
                  id: p.id!,
                  name: cannedJob.idToInventoryProducts![
                    p.info!.inventoryProduct!
                  ]!.info!.name!,
                  quantity:
                    p.info!.quantity ||
                    cannedJob.idToInventoryProducts![p.info!.inventoryProduct!]
                      .info!.quantity ||
                    1
                })),
                ...cannedJob.motorFluids!.map((p) => ({
                  id: p.id!,
                  name: p.info!.name!,
                  quantity: undefined,
                  secondaryText: 'Motor Fluids'
                }))
              ].map((data) => (
                <CustomListItem
                  key={data.id!}
                  id={data.id!}
                  data={data}
                  handleClick={handleClickSub || (() => {})}
                  checked={
                    !!(
                      selectedCanned && !selectedCanned.unSelected.has(data.id!)
                    )
                  }
                  disableSelectSubItem={!handleClickSub}
                />
              ))}
            {cannedJob.customFees !== undefined &&
              cannedJob.customFees?.length > 0 && (
                <ListItem selected={true}>
                  <Typography variant="subtitle2">Fees/Discounts</Typography>
                </ListItem>
              )}
            {cannedJob.customFees?.map((fee) => (
              <ListItem disableGutters disablePadding key={fee.id}>
                <ListItemText
                  primary={
                    '- ' +
                    (fee.info?.numDollars && fee.info?.numDollars < 0
                      ? '(discount) $'
                      : '(fee) $') +
                    fee.info?.numDollars
                  }
                  secondary={fee.info?.name}
                />
              </ListItem>
            ))}
            {cannedJob.related !== undefined &&
              cannedJob.related?.length > 0 && (
                <ListItem selected={true}>
                  <Typography variant="subtitle2">Related</Typography>
                </ListItem>
              )}
            {relatedCannedJobExpanded !== undefined &&
              selectedRelated !== undefined &&
              cannedJob.related!.map((r) => {
                const filteredEntry = relatedCannedJobExpanded.find(
                  (e) => e.entry!.id === r.info!.child
                )!

                return (
                  <CustomListItem
                    key={
                      'related' +
                      cannedJob.entry!.id! +
                      filteredEntry.entry!.id!
                    }
                    id={
                      'related' +
                      cannedJob.entry!.id! +
                      filteredEntry.entry!.id!
                    }
                    data={{
                      id: filteredEntry.entry!.id!,
                      name: filteredEntry.entry!.info!.name!,
                      secondaryText: filteredEntry.entry!.info!.description!
                    }}
                    handleClick={handleClickRelated || (() => {})}
                    checked={selectedRelated.some(
                      (r) => r === filteredEntry.entry!.id
                    )}
                    disableSelectSubItem={!handleClickRelated}
                  />
                )
              })}
          </List>
        </>
      }
    />
  )
}
